/**
 * Presets and labels of the rich-text editor.
 *
 * Colors here are mail *content* colors (they end up in the sent HTML and must look the same in
 * every mail client), not UI colors, so they are fixed hex values (Office's standard palette)
 * instead of design tokens. "Automatic" removes the color, so text follows the reader's theme.
 */
import { createTranslator, type MessageKey, type Translate } from '@platform/i18n';

export const FONT_SIZES = [
  { id: 'small', value: '9pt' },
  { id: 'normal', value: null },
  { id: 'large', value: '14pt' },
  { id: 'huge', value: '18pt' },
] as const;
export type FontSizeId = (typeof FONT_SIZES)[number]['id'];

export const TEXT_COLORS = [
  { id: 'gray', value: '#595959' },
  { id: 'red', value: '#c00000' },
  { id: 'orange', value: '#e36c09' },
  { id: 'green', value: '#00b050' },
  { id: 'blue', value: '#0070c0' },
  { id: 'darkBlue', value: '#002060' },
  { id: 'purple', value: '#7030a0' },
] as const;

export const HIGHLIGHT_COLORS = [
  { id: 'yellow', value: '#ffff00' },
  { id: 'brightGreen', value: '#00ff00' },
  { id: 'turquoise', value: '#00ffff' },
  { id: 'pink', value: '#ff00ff' },
  { id: 'lightGray', value: '#d9d9d9' },
] as const;

export type ColorId = (typeof TEXT_COLORS)[number]['id'] | (typeof HIGHLIGHT_COLORS)[number]['id'];

export const ALIGNMENTS = ['left', 'center', 'right', 'justify'] as const;
export type Alignment = (typeof ALIGNMENTS)[number];

export interface RichTextEditorLabels {
  toolbar: string;
  moreFormatting: string;
  lessFormatting: string;
  bold: string;
  italic: string;
  underline: string;
  strikethrough: string;
  fontSize: string;
  fontSizes: Record<FontSizeId, string>;
  textColor: string;
  highlight: string;
  automaticColor: string;
  noHighlight: string;
  colors: Record<ColorId, string>;
  bulletList: string;
  orderedList: string;
  indent: string;
  outdent: string;
  quote: string;
  link: string;
  linkInsertTitle: string;
  linkEditTitle: string;
  linkUrl: string;
  linkText: string;
  linkSave: string;
  linkRemove: string;
  linkInvalid: string;
  align: string;
  alignments: Record<Alignment, string>;
  clearFormatting: string;
  undo: string;
  redo: string;
  image: string;
  /** Contains `{name}`. */
  imageUnsupported: string;
  /** Contains `{name}` and `{max}`. */
  imageTooLarge: string;
  plainText: string;
  richText: string;
  plainTextConfirmTitle: string;
  plainTextConfirmDescription: string;
  plainTextConfirm: string;
  modeLabel: string;
  modeFormatted: string;
  modeSource: string;
  modePlain: string;
  sourceLabel: string;
  sourceUnsupported: string;
  sourceConfirmTitle: string;
  sourceConfirmDescription: string;
  sourceConfirm: string;
  insertTable: string;
  /** Contains `{rows}` and `{cols}`. */
  tableSize: string;
  tableOptions: string;
  rowAbove: string;
  rowBelow: string;
  deleteRow: string;
  columnBefore: string;
  columnAfter: string;
  deleteColumn: string;
  mergeCells: string;
  splitCell: string;
  deleteTable: string;
  tableBorders: string;
  bordersNone: string;
  bordersLight: string;
  bordersFull: string;
  tablePadding: string;
  paddingCompact: string;
  paddingNormal: string;
  cellAlign: string;
  alignTop: string;
  alignMiddle: string;
  alignBottom: string;
  horizontalRule: string;
  pictureOptions: string;
  pictureSize: string;
  sizeSmall: string;
  sizeMedium: string;
  sizeOriginal: string;
  picturePlacement: string;
  placementInline: string;
  placementLeft: string;
  cancel: string;
}

type FlatLabel = Exclude<keyof RichTextEditorLabels, 'fontSizes' | 'colors' | 'alignments'>;

const FLAT_LABELS: readonly FlatLabel[] = [
  'toolbar', 'moreFormatting', 'lessFormatting', 'bold', 'italic', 'underline', 'strikethrough', 'fontSize', 'textColor', 'highlight', 'automaticColor',
  'noHighlight', 'bulletList', 'orderedList', 'indent', 'outdent', 'quote', 'link', 'linkInsertTitle', 'linkEditTitle',
  'linkUrl', 'linkText', 'linkSave', 'linkRemove', 'linkInvalid', 'align', 'clearFormatting', 'undo', 'redo', 'image',
  'imageUnsupported', 'imageTooLarge', 'plainText', 'richText', 'plainTextConfirmTitle', 'plainTextConfirmDescription',
  'plainTextConfirm', 'modeLabel', 'modeFormatted', 'modeSource', 'modePlain', 'sourceLabel', 'sourceUnsupported',
  'sourceConfirmTitle', 'sourceConfirmDescription', 'sourceConfirm', 'insertTable', 'tableSize', 'tableOptions',
  'rowAbove', 'rowBelow', 'deleteRow', 'columnBefore', 'columnAfter', 'deleteColumn', 'mergeCells', 'splitCell',
  'deleteTable', 'tableBorders', 'bordersNone', 'bordersLight', 'bordersFull', 'tablePadding', 'paddingCompact',
  'paddingNormal', 'cellAlign', 'alignTop', 'alignMiddle', 'alignBottom', 'horizontalRule', 'pictureOptions',
  'pictureSize', 'sizeSmall', 'sizeMedium', 'sizeOriginal', 'picturePlacement', 'placementInline', 'placementLeft',
  'cancel',
];

/**
 * Editor labels from the language packs (keys `editor.*`). Placeholders stay in the text
 * (`{name}`, `{max}`): the editor fills them itself.
 */
export function editorLabelsFor(t: Translate): RichTextEditorLabels {
  const key = (path: string) => t(`editor.${path}` as MessageKey, { name: '{name}', max: '{max}', rows: '{rows}', cols: '{cols}' });
  const record = <K extends string>(group: string, ids: readonly K[]) =>
    Object.fromEntries(ids.map((id) => [id, key(`${group}.${id}`)])) as Record<K, string>;
  return {
    ...(Object.fromEntries(FLAT_LABELS.map((name) => [name, key(name)])) as Record<FlatLabel, string>),
    fontSizes: record('fontSizes', FONT_SIZES.map((size) => size.id)),
    colors: record('colors', [...TEXT_COLORS, ...HIGHLIGHT_COLORS].map((color) => color.id)),
    alignments: record('alignments', ALIGNMENTS),
  };
}

/** English labels from the reference pack (tests, stories, apps without PlatformProvider). */
export const DEFAULT_EDITOR_LABELS: RichTextEditorLabels = editorLabelsFor(createTranslator('en'));
